import React, { useState, useEffect } from 'react';
import RichTextEditor from '../../../shared/components/RichTextEditor';
import ImagePicker from '../../../shared/components/ImagePicker';
import { uploadToCloudinary } from '../../../shared/utils/cloudinaryUpload';
import { Select, MenuItem, FormControl, InputLabel, TextField, Button, CircularProgress, Snackbar, Alert, FormControlLabel, Switch } from '@mui/material';
import { BiSolidPaperPlane } from 'react-icons/bi';
import useArticlesViewModel from '../hooks/useArticlesViewModel';
import GetArticleCategoriesUseCase from '../use-cases/GetArticleCategoriesUseCase';
import FirebaseArticleRepository from '../api/FirebaseArticleRepository';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../../shared/components/Backbtn';
import '../components/articles.css';
import NotificationService from '../../../shared/utils/notificationUtils';

const AddArticleWithCloudinary = () => {
  const navigate = useNavigate();
  const { addArticle, error: vmError } = useArticlesViewModel();
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [linkName, setLinkName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isTrending, setIsTrending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quickReadsCategories, setQuickReadsCategories] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  
  useEffect(() => {
    const getArticleCategoriesUseCase = new GetArticleCategoriesUseCase(new FirebaseArticleRepository());
    const fetchCategories = async () => {
      try {
        const categories = await getArticleCategoriesUseCase.execute();
        setQuickReadsCategories(categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setSnackbarMessage('Failed to fetch categories.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };
    fetchCategories();
  }, []);

  const handleSubmit = async () => {
    if (!category || !title || !description.trim()) {
      setSnackbarMessage('Please fill in all required fields (Category, Title, and Description).');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
  
    setLoading(true);

    try {
      let imageUrl = null;
      if (selectedFile) {
        imageUrl = await uploadToCloudinary(selectedFile);
      }

      const articleData = {
        category,
        title,
        link,
        linkName,
        description,
        imageUrl,
        isTrending,
      };

      const newArticle = await addArticle(articleData);

      // Send notification
      try {
        await NotificationService.sendToTopic({
          topic: 'articles',
          title: category,
          body: title,
          category: 'article',
          data: { articleId: newArticle.id },
        });
      } catch (notificationError) {
        console.error('Notification error (article still created):', notificationError);
      }
  
      setSnackbarMessage('Article added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
  
      setCategory("");
      setTitle("");
      setLink("");
      setLinkName("");
      setDescription("");
      setSelectedFile(null);
      setIsTrending(false);
      
      setTimeout(() => {
        navigate('/articles');
      }, 1500);
    } catch (error) {
      console.error('Error:', error);
      setSnackbarMessage(`Failed to add article: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (
    <div className="p-4">
      <div className="flex justify-end gap-3 mb-4">
        <BackButton tooltip="Back" />
      </div>
      
      <Snackbar
        open={snackbarOpen || !!vmError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage || vmError?.message || vmError}
        </Alert>
      </Snackbar>

      <div className="section">
        <h2 className="text-2xl font-bold mb-4">Add Article</h2>

        <FormControl variant="outlined" fullWidth className="form-control">
          <InputLabel id="category-label">Category</InputLabel>
          <Select
            labelId="category-label"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            label="Category"
          >
            {quickReadsCategories.length > 0 ? (
              quickReadsCategories.map((cat, index) => (
                <MenuItem key={index} value={cat.toLowerCase()}>{cat}</MenuItem>
              ))
            ) : (
              <MenuItem disabled>No categories available</MenuItem>
            )}
          </Select>
        </FormControl>

        <FormControl fullWidth className="form-control">
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} variant="outlined" required />
        </FormControl>

        <FormControl fullWidth className="form-control">
          <TextField label="Link (Optional)" value={link} onChange={(e) => setLink(e.target.value)} variant="outlined" />
        </FormControl>

        <FormControl fullWidth className="form-control">
          <TextField label="Link Name (Optional)" value={linkName} onChange={(e) => setLinkName(e.target.value)} variant="outlined" placeholder="e.g. Apply, Read More, Visit" />
        </FormControl>

        <ImagePicker
          label="Article Image (Optional)"
          onFileSelect={(file) => setSelectedFile(file)}
        />

        <FormControlLabel
          control={<Switch checked={isTrending} onChange={(e) => setIsTrending(e.target.checked)} />}
          label="Mark as Trending"
          sx={{ mt: 2 }}
        />

        <div className="mt-4">
          <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1, color: 'rgba(0,0,0,0.6)', fontSize: '0.875rem' }}>
            Description <span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <RichTextEditor value={description} onChange={setDescription} placeholder="Write your article content here..." minHeight="300px" />
        </div>

        <Button
          variant="contained"
          endIcon={<BiSolidPaperPlane />}
          onClick={handleSubmit}
          disabled={loading}
          fullWidth
          sx={{ mt: 3 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Publish Article'}
        </Button>
      </div>
    </div>
  );
};

export default AddArticleWithCloudinary;
